import { PropTypes } from "prop-types";
import { useEffect, useMemo, useRef } from "react";
import { useStaticQuery, graphql } from "gatsby";

import { connectStateResults } from "react-instantsearch-dom";

import useResponsiveWithHydrationFix from "../../../hooks/useResponsiveWithHydrationFix";
import { useDebounce } from "../../../hooks/useDebounce";
import { useCurrentLang } from "../../../hooks/useLocalizedCartInfo";
import { useTracking } from "../../../context/Tracking";

import { ComponentType } from "@constants/ComponentType";
import { ALButtonV2 } from "../../ALComponents";
import ALLogo from "../../al_components/ALLogo";
import SortDropdown from "@components/CollectionPage/SortDropdown";
import CustomHitCount from "./customSearchUI/CustomHitCount";
import CustomInfiniteHits from "./customSearchUI/CustomInfiniteHits";
import CustomSearchBox from "./customSearchUI/CustomSearchBox";
import TopSearches from "./customSearchUI/TopSearches";
import IconClose from "@svgs/close-delete.svg";
import ScrollToTopButton from "@components/ScrollToTopButton";

const ORDER_NUMBER_REGEX = /18\d*6/;

function CustomSearchUI({
  searchState,
  searchResults,
  sortBy,
  onChangeSortBy,
  onCardClick,
  indexName,
  closeSearch,
  allSearchResults,
}) {
  const hits = allSearchResults?.hits || [];
  const lang = useCurrentLang();
  const searchUIRef = useRef(null);

  const contentfulData = useStaticQuery(graphql`
    query searchLandingPage {
      allContentfulSearchLandingPage {
        nodes {
          node_locale
          title
          categories
          links {
            altText
            desktopImage {
              gatsbyImageData(width: 120, height: 120)
              file {
                url
              }
            }
            mobileImage {
              gatsbyImageData(width: 90, height: 90)
              file {
                url
              }
            }
            link {
              title
              link {
                link
              }
            }
          }
          cards {
            title
            image {
              altText
              link {
                title
                link {
                  link
                }
              }
              desktopImage {
                file {
                  url
                }
              }
              mobileImage {
                file {
                  url
                }
              }
            }
            textColor {
              mobile
              desktop
            }
          }
        }
      }
    }
  `);

  const searchData = useMemo(() => {
    let data = null;
    if (
      contentfulData.allContentfulSearchLandingPage.nodes &&
      contentfulData.allContentfulSearchLandingPage.nodes.length > 0
    ) {
      const defaultEntry = contentfulData.allContentfulSearchLandingPage.nodes.filter(
        (card) => card.node_locale === "en-US"
      );
      const localeSpecificEntry = contentfulData.allContentfulSearchLandingPage.nodes.filter(
        (card) => card.node_locale === lang
      );
      data = localeSpecificEntry ?? defaultEntry;
    }
    return data.find((entry) => !entry.title.includes("null"));
  }, [contentfulData.allContentfulSearchLandingPage.nodes, lang]);

  const separator = useResponsiveWithHydrationFix(
    <hr className="border-0 m-0 border-t border-dark-grey w-full" />,
    null
  );

  const header = useResponsiveWithHydrationFix(
    <div className="flex items-center justify-between border-b border-dark-grey py-[18px] px-[15px]">
      <ALLogo className="w-[100px]" />
      <button type="button" onClick={closeSearch}>
        <IconClose fill="none" stroke="#000000" width="15px" height="15px" />
      </button>
    </div>,
    null
  );

  const debouncedQuery = useDebounce(searchState?.query, 500);
  const { trackSearch } = useTracking();

  useEffect(() => {
    if (debouncedQuery) {
      if (searchResults) {
        trackSearch({ query: debouncedQuery, searchResults: searchResults.hits });
      }
    }
  }, [debouncedQuery, trackSearch]);

  // close search modal on escape key press
  useEffect(() => {
    const close = (e) => {
      if (e.keyCode === 27) {
        closeSearch();
      }
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [closeSearch]);

  const isOrderFormat = ORDER_NUMBER_REGEX.test(searchState?.query);

  let orderNumber;
  if (isOrderFormat) {
    orderNumber = searchState.query.match(ORDER_NUMBER_REGEX)[0];
  }

  return (
    <div
      ref={searchUIRef}
      data-testid="searchui"
      className="overflow-y-scroll overflow-x-hidden bg-white relative h-[100vh]"
    >
      {header}
      {separator}
      <div className="max-w-[1140px] mx-auto pt-[30px] pb-[30px] w-full mobile:pt-[15px] mobile:pb-[15px]">
        <CustomSearchBox closeSearch={closeSearch} hits={hits} />
        {!isOrderFormat && (
          <TopSearches
            searchQuery={searchState?.query}
            topCategories={searchData.categories}
            cards={searchData.cards}
            nbHits={searchResults?.nbHits}
            links={searchData.links}
          />
        )}
        {isOrderFormat && (
          <div key="orderFormatSection">
            <p className="typo-body mb-[20px]">Looking for an order? Use the link below</p>
            <ALButtonV2
              size="medium"
              variant="primary"
              type="button"
              fullWidth
              onClick={() => {
                window.location.href = `/track-my-order/?orderNo=${orderNumber}`;
              }}
            >
              Track My Order
            </ALButtonV2>
          </div>
        )}
        {searchResults?.nbHits > 0 && searchState?.query && (
          <div className="flex items-center justify-end mt-[30px] mobile:mt-[0px] mobile:justify-between mobile:px-[15px]">
            <div className="text-dark-grey typo-micro mobile:typo-body mr-[15px] mobile:text-black">
              <CustomHitCount />
              &nbsp;products
            </div>
            <SortDropdown
              onSort={(s) => typeof onChangeSortBy === "function" && onChangeSortBy(s)}
              sortingType={sortBy}
              componentType={ComponentType.SEARCH}
            />
          </div>
        )}
      </div>
      {/* Search results */}
      {searchState?.query && (
        <CustomInfiniteHits
          onCardClick={onCardClick}
          indexName={indexName}
          queryId={searchResults?.queryID}
        />
      )}
      <ScrollToTopButton containerRef={searchUIRef} />
    </div>
  );
}

CustomSearchUI.propTypes = {
  searchState: PropTypes.object,
  searchResults: PropTypes.object,
  sortBy: PropTypes.string,
  onChangeSortBy: PropTypes.func,
  onCardClick: PropTypes.func,
  indexName: PropTypes.string,
  closeSearch: PropTypes.func,
  allSearchResults: PropTypes.object,
};

export default connectStateResults(CustomSearchUI);
